import { normalizePeriodBuckets, resolveAttributeValueForWeek } from './periodizationPeriodBuckets';
import {
  getNumericRange,
  normalizeStoredValue,
  resolveSchemaForType,
} from './periodizationValueSchema';

export function weekIndexInBlock(weekStart, blockStart, blockEnd, weeks) {
  const start = new Date(`${weekStart}T12:00:00`);
  const bStart = new Date(`${blockStart}T12:00:00`);
  const bEnd = new Date(`${blockEnd}T12:00:00`);
  if (start > bEnd || start < bStart) return -1;
  return weeks.indexOf(weekStart);
}

export function resolveEditableTarget(attr, block, phase, micro) {
  const enabled = normalizePeriodBuckets(attr.period_buckets);
  if (!phase && !block) return { editable: false };
  if (enabled.week && micro) {
    return { editable: true, targetType: 'micro', microId: micro.id, phaseId: phase?.id, blockId: block?.id };
  }
  if (enabled.phase && phase) {
    return { editable: true, targetType: 'phase', phaseId: phase.id, microId: micro?.id || null, blockId: block?.id };
  }
  if (enabled.block && block) {
    return { editable: true, targetType: 'block', blockId: block.id, phaseId: phase?.id, microId: micro?.id || null };
  }
  return { editable: false };
}

export function buildAttributeWeekSeries(block, weeks, attr) {
  const rows = [];
  for (const w of weeks) {
    if (weekIndexInBlock(w, block.start_date, block.end_date, weeks) < 0) continue;
    const { phase, micro } = findWeekContextInBlock(block, w);
    if (!phase) continue;
    const resolved = resolveAttributeValueForWeek({
      typeKey: attr.type_key,
      enabledBuckets: attr.period_buckets,
      block,
      phase,
      micro,
    });
    const edit = resolveEditableTarget(attr, block, phase, micro);
    rows.push({
      weekStart: w,
      weekLabel: w.slice(5),
      weekNumber: micro?.week_number_in_block ?? null,
      phaseId: phase.id,
      phaseName: phase.name,
      microId: micro?.id ?? null,
      value: resolved.value,
      bucket: resolved.bucket,
      ...edit,
    });
  }
  return rows;
}

function findWeekContextInBlock(block, weekStartYmd) {
  for (const phase of block.phases || []) {
    if (weekStartYmd < phase.start_date || weekStartYmd > phase.end_date) continue;
    const micro = phase.microcycles?.find(
      (m) => weekStartYmd >= m.week_start && weekStartYmd <= m.week_end
    );
    return { phase, micro };
  }
  return { phase: null, micro: null };
}

export { findWeekContextInBlock };

function scopeGroupKey(bucket, phaseId, value) {
  if (!bucket || bucket === 'week') return null;
  const valueKey = value === undefined ? 'null' : JSON.stringify(value);
  if (bucket === 'phase') return `phase:${phaseId ?? ''}:${valueKey}`;
  return `block:${valueKey}`;
}

/** Per-week resolved value + span role for scope connector UI in calendar grids. */
export function buildAttributeWeekScopeMeta(block, weeks, attr, { isWeekInRange } = {}) {
  const meta = [];
  for (const w of weeks) {
    const inRange = isWeekInRange ? isWeekInRange(w, block) : true;
    const { phase, micro } = inRange ? findWeekContextInBlock(block, w) : { phase: null, micro: null };
    const resolved =
      inRange && phase
        ? resolveAttributeValueForWeek({
            typeKey: attr.type_key,
            enabledBuckets: attr.period_buckets,
            block,
            phase,
            micro,
          })
        : { value: null, bucket: null };
    meta.push({
      weekStart: w,
      inRange,
      phaseId: phase?.id ?? null,
      value: resolved.value,
      bucket: resolved.bucket,
      groupKey:
        inRange && phase ? scopeGroupKey(resolved.bucket, phase?.id, resolved.value) : null,
    });
  }

  for (let i = 0; i < meta.length; i += 1) {
    const cell = meta[i];
    if (!cell.inRange || !cell.groupKey) {
      cell.spanRole = 'none';
      continue;
    }
    const prevSame = i > 0 && meta[i - 1].inRange && meta[i - 1].groupKey === cell.groupKey;
    const nextSame =
      i + 1 < meta.length && meta[i + 1].inRange && meta[i + 1].groupKey === cell.groupKey;
    if (prevSame && nextSame) cell.spanRole = 'middle';
    else if (prevSame) cell.spanRole = 'end';
    else if (nextSame) cell.spanRole = 'start';
    else cell.spanRole = 'single';
  }

  return meta;
}

export function isDiscreteAttributeType(attr) {
  const schema = resolveSchemaForType(attr);
  const storage = schema?.config?.storage;
  return storage === 'boolean' || storage === 'lookup_key';
}

export function getDiscreteBuckets(attr) {
  const schema = resolveSchemaForType(attr);
  if (schema.config?.storage === 'boolean') {
    const b = schema.config.boolean || {};
    return [
      { key: true, label: b.true_label || 'Yes', color: b.true_color, icon: b.true_icon },
      { key: false, label: b.false_label || 'No', color: b.false_color, icon: b.false_icon },
    ];
  }
  if (schema.config?.storage === 'lookup_key') {
    return (schema.config.lookup?.options || []).map((o) => ({
      key: o.key,
      label: o.label,
      color: o.color,
      icon: o.icon,
      numeric_value: o.numeric_value,
    }));
  }
  return [];
}

export function normalizeAttrValue(value, attr) {
  return normalizeStoredValue(value, attr);
}

export function getNumericRangeForAttr(attr) {
  return getNumericRange(attr);
}

/** Persist draft values; returns block id for reload. */
export async function persistAttributeWeekValues(api, attr, series, draftByWeek) {
  const microPatches = {};
  const phasePatches = {};
  let blockPatch = null;
  let blockId = null;

  for (const row of series) {
    if (!row.editable) continue;
    if (!draftByWeek.has(row.weekStart)) continue;
    const newVal = normalizeAttrValue(draftByWeek.get(row.weekStart), attr);
    if (row.targetType === 'micro' && row.microId) {
      microPatches[row.microId] = {
        ...(microPatches[row.microId] || {}),
        [attr.type_key]: newVal,
      };
    } else if (row.targetType === 'phase' && row.phaseId) {
      phasePatches[row.phaseId] = {
        ...(phasePatches[row.phaseId] || {}),
        [attr.type_key]: newVal,
      };
    } else if (row.targetType === 'block' && row.blockId) {
      blockId = row.blockId;
      blockPatch = { ...(blockPatch || {}), [attr.type_key]: newVal };
    }
  }

  const blockPromise =
    blockId && blockPatch
      ? api.get(`/api/training/periodization/blocks/${blockId}`).then((res) =>
          api.put(`/api/training/periodization/blocks/${blockId}`, {
            attributes: { ...(res.data?.block?.attributes || {}), ...blockPatch },
          })
        )
      : Promise.resolve();

  await Promise.all([
    blockPromise,
    ...Object.entries(microPatches).map(([id, attributes]) =>
      api.put(`/api/training/periodization/microcycles/${id}`, { attributes })
    ),
    ...Object.entries(phasePatches).map(([id, attributes]) =>
      api.put(`/api/training/periodization/phases/${id}`, { attributes })
    ),
  ]);
}

export function applySelectionRange(weeks, anchor, target, selected) {
  const ai = weeks.indexOf(anchor);
  const ti = weeks.indexOf(target);
  if (ai < 0 || ti < 0) return selected;
  const lo = Math.min(ai, ti);
  const hi = Math.max(ai, ti);
  const next = new Set(selected);
  for (let i = lo; i <= hi; i += 1) next.add(weeks[i]);
  return next;
}
